import React, { useState } from 'react';
import styled from 'styled-components';
import { AddLiquidityContainer } from '../components/liquidity/AddLiquidityContainer';
import { MavrkPoolsList } from '../components/liquidity/MavrkPoolsList';
import { PositionSearch } from '../components/liquidity/PositionSearch';
import { TVLChart } from '../components/liquidity/TVLChart';
import PortfolioPreview from '../components/discover/PortfolioPreview';
import CustomSwapWidget from '../components/swap/CustomSwapWidget';
import { useSinglePoolTVL } from '../hooks/usePoolTVL';
import { useCumulativePoolTVL } from '../hooks/useCumulativePoolTVL';
import type { PoolWithTVL } from '../hooks/useMavrkPools';
import type { SearchResult } from '../hooks/usePositionSearch';

// ============================================
// STYLED COMPONENTS
// ============================================

const PageContainer = styled.div`
  padding: 24px;
  background: transparent;
  min-height: calc(100vh - 64px - 56px);
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CumulativeTVLContainer = styled.div`
  background: linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 1px solid rgba(96, 165, 250, 0.2);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 20px;
    margin-bottom: 24px;
  }
`;

const CumulativeTVLLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const CumulativeTVLValue = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: #60a5fa;
  margin-bottom: 8px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const CumulativeTVLDescription = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const SkeletonValue = styled.div`
  height: 48px;
  width: 200px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.1) 25%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.1) 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin: 0 auto 8px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
  
  @media (max-width: 768px) {
    height: 36px;
    width: 150px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  
  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 1200px) {
    width: 100%;
  }
`;

const ThreeContainerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  
  @media (max-width: 1400px) {
    grid-template-columns: 1fr;
  }
  
  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const ContainerCard = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 20px;
  min-height: 500px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
  overflow: hidden;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 1400px) {
    min-height: 400px;
    max-height: none;
  }
  
  @media (max-width: 768px) {
    padding: 16px;
    min-height: auto;
  }
`;

const RightSidebar = styled.aside`
  width: 380px;
  min-width: 380px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (max-width: 1200px) {
    width: 100%;
    min-width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    
    & > * {
      flex: 1;
      min-width: 300px;
    }
  }
  
  @media (max-width: 700px) {
    flex-direction: column;
    
    & > * {
      min-width: 100%;
    }
  }
`;

const SidebarSection = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 16px;
`;

const SidebarTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const DetailsCard = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 24px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
    margin-top: 16px;
  }
`;

const DetailsTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const DetailValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  
  &.mono {
    font-family: 'Courier New', monospace;
    font-size: 12px;
  }
  
  &.highlight {
    color: #60a5fa;
  }
`;

/**
 * Unified Liquidity Management Page
 * 
 * Combines three key functionalities:
 * 1. Add Liquidity / Create V3 Pools
 * 2. View Mavrk Pools List (with TVL)
 * 3. Search Positions (by name, NFT ID, or contract address)
 */
export const UnifiedLiquidityPage: React.FC = () => {
  const [selectedPool, setSelectedPool] = useState<PoolWithTVL | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<SearchResult | null>(null);
  
  // Only calculate TVL for the selected pool (on-demand)
  const { tvl, loading: tvlLoading, error: tvlError } = useSinglePoolTVL(selectedPool?.pool_address || null);
  
  // Fetch cumulative TVL for all Ink pools
  const { cumulativeTVL, poolCount, loading: cumulativeLoading } = useCumulativePoolTVL();

  const handlePoolSelect = (pool: PoolWithTVL) => {
    setSelectedPool(pool);
    setSelectedPosition(null);
    console.log('Selected pool:', pool);
  };

  const handlePositionSelect = (position: SearchResult) => {
    setSelectedPosition(position);
    setSelectedPool(null);
    console.log('Selected position:', position);
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Liquidity Management</PageTitle>
        <PageSubtitle>
          Create V3 pools, view Mavrk token pools, and manage your positions on Ink chain
        </PageSubtitle>
      </PageHeader>

      {/* Cumulative TVL Display */}
      <CumulativeTVLContainer>
        <CumulativeTVLLabel>Total Mavrk Pools TVL</CumulativeTVLLabel>
        {cumulativeLoading ? (
          <SkeletonValue />
        ) : (
          <CumulativeTVLValue>
            ${cumulativeTVL.toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}
          </CumulativeTVLValue>
        )}
        <CumulativeTVLDescription>
          {poolCount > 0 
            ? `Total Value Locked across ${poolCount} pool${poolCount !== 1 ? 's' : ''} on Ink chain`
            : 'Calculating total value locked...'
          }
        </CumulativeTVLDescription>
      </CumulativeTVLContainer>

      {/* TVL History Chart */}
      <TVLChart />

      <ContentWrapper>
        <MainContent>
          {/* Three Container Layout */}
          <ThreeContainerGrid>
            {/* Container 1: Add Liquidity */}
            <ContainerCard>
              <AddLiquidityContainer
                onPoolCreated={(poolAddress) => {
                  console.log('Pool created:', poolAddress);
                  // Refresh pools list
                }}
              />
            </ContainerCard>

            {/* Container 2: Mavrk Pools List */}
            <ContainerCard>
              <MavrkPoolsList onPoolSelect={handlePoolSelect} />
            </ContainerCard>

            {/* Container 3: Position Search */}
            <ContainerCard>
              <PositionSearch onPositionSelect={handlePositionSelect} />
            </ContainerCard>
          </ThreeContainerGrid>

          {/* Selected Pool/Position Details */}
          {(selectedPool || selectedPosition) && (
            <DetailsCard>
              <DetailsTitle>Selected Details</DetailsTitle>
              
              {selectedPool && (
                <DetailsGrid>
                  <DetailItem>
                    <DetailLabel>Token Name</DetailLabel>
                    <DetailValue>{selectedPool.name}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Symbol</DetailLabel>
                    <DetailValue>{selectedPool.symbol}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Contract Address</DetailLabel>
                    <DetailValue className="mono">{selectedPool.contract_address}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Pool Address</DetailLabel>
                    <DetailValue className="mono">{selectedPool.pool_address || 'N/A'}</DetailValue>
                  </DetailItem>
                  {selectedPool.pool_address && (
                    <DetailItem>
                      <DetailLabel>Total Value Locked (TVL)</DetailLabel>
                      <DetailValue className="highlight">
                        {tvlLoading ? (
                          'Calculating...'
                        ) : tvlError ? (
                          'Error calculating TVL'
                        ) : tvl !== null ? (
                          `$${tvl.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          })}`
                        ) : (
                          'N/A'
                        )}
                      </DetailValue>
                    </DetailItem>
                  )}
                  {selectedPool.nft_token_id && (
                    <DetailItem>
                      <DetailLabel>NFT Position ID</DetailLabel>
                      <DetailValue className="highlight">#{selectedPool.nft_token_id}</DetailValue>
                    </DetailItem>
                  )}
                </DetailsGrid>
              )}

              {selectedPosition && (
                <DetailsGrid>
                  <DetailItem>
                    <DetailLabel>Token Name</DetailLabel>
                    <DetailValue>{selectedPosition.name}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Symbol</DetailLabel>
                    <DetailValue>{selectedPosition.symbol}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Contract Address</DetailLabel>
                    <DetailValue className="mono">{selectedPosition.contract_address}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Pool Address</DetailLabel>
                    <DetailValue className="mono">{selectedPosition.pool_address || 'N/A'}</DetailValue>
                  </DetailItem>
                  {selectedPosition.nft_token_id && (
                    <DetailItem>
                      <DetailLabel>NFT Position ID</DetailLabel>
                      <DetailValue className="highlight">#{selectedPosition.nft_token_id}</DetailValue>
                    </DetailItem>
                  )}
                  <DetailItem>
                    <DetailLabel>Matched By</DetailLabel>
                    <DetailValue>{selectedPosition.matchType}</DetailValue>
                  </DetailItem>
                </DetailsGrid>
              )}
            </DetailsCard>
          )}
        </MainContent>

        {/* Right Sidebar: Portfolio and Swap */}
        <RightSidebar>
          <SidebarSection>
            <SidebarTitle>📊 Portfolio</SidebarTitle>
            <PortfolioPreview />
          </SidebarSection>
          
          <SidebarSection>
            <SidebarTitle>💱 Swap</SidebarTitle>
            <CustomSwapWidget />
          </SidebarSection>
        </RightSidebar>
      </ContentWrapper>
    </PageContainer>
  );
};

export default UnifiedLiquidityPage;

